'use client';

import { FC, PropsWithChildren, useState } from 'react';
import { TextField } from '../components/atoms/text-field';
import { PhoneNumberInput } from '../components/PhoneNumberInput';
import { Button, buttonStyles } from '../components/atoms/button';
import Link from 'next/link';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { getFontVariable, getRoundedClassName } from '../components/utils';
import {
  PageCollectionConfigFontFamily,
  PageCollectionConfigLanguage,
  PageCollectionConfigRadiusClassName,
  PageFieldsetFieldType,
  PageFieldsetFieldTypeEnum,
} from '@creator-suite/page-types';

import { TypeEnumIncentiveType } from '@creator-suite/enums';
import { CircleAlertIcon } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FIRST_NAME_MAX_LENGTH,
  getInsertLeadToPageSchemaByLanguage,
  HOUSE_NUMBER_MAX_LENGTH,
  InsertLeadToPageFormSchemaType,
  LAST_NAME_MAX_LENGTH,
  STREET_MAX_LENGTH,
} from '@/data/leads-dto';
import { getLabelForField, getPlaceholderForField } from '@/form-utils';

export interface InsertLeadToPageFormProps {
  language: PageCollectionConfigLanguage;
  accountDisplayName: string;
  organizationLegalName: string;

  handleSubmit: (values: InsertLeadToPageFormSchemaType) => Promise<string>;
  onFormError: Parameters<
    ReturnType<typeof useForm<InsertLeadToPageFormSchemaType>>['handleSubmit']
  >[1];
  success?: boolean;
  title: string;
  description: string | null | undefined;
  buttonRadiusClassName: PageCollectionConfigRadiusClassName;
  inputRadiusClassName: PageCollectionConfigRadiusClassName;
  cardRadiusClassName: PageCollectionConfigRadiusClassName;
  buttonText: string;
  fontFamily: PageCollectionConfigFontFamily;
  fieldset: PageFieldsetFieldType[];

  // Logo
  link?: string | null | undefined;
  error?: string | null | undefined;
  incentiveType: TypeEnumIncentiveType;

  externalPrivacyPolicyUrl?: string;
  countryCode?: string;
}

export const InsertLeadToPageForm: FC<
  PropsWithChildren<InsertLeadToPageFormProps>
> = (props) => {
  const {
    language = PageCollectionConfigLanguage.DE,
    children,
    handleSubmit,
    onFormError,
    success,
    buttonRadiusClassName,
    inputRadiusClassName,
    buttonText,
    fontFamily,
    fieldset = [],
    accountDisplayName: artistDisplayName,
    organizationLegalName: organizationName,
    error,
    incentiveType,
    externalPrivacyPolicyUrl,
    countryCode,
  } = props;

  const form = useForm<InsertLeadToPageFormSchemaType>({
    resolver: zodResolver(getInsertLeadToPageSchemaByLanguage(language)),
  });

  const {
    register,
    formState: { errors, isSubmitting },
  } = form;

  const [link, setLink] = useState<string | undefined>(undefined);

  const fieldZip = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.ZIP,
  );
  const fieldEmail = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.EMAIL,
  );
  const fieldCity = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.CITY,
  );
  const fieldStreet = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.STREET,
  );
  const fieldHouseNumber = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.HOUSE_NUMBER,
  );
  const fieldFirstName = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.FIRST_NAME,
  );
  const fieldLastName = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.LAST_NAME,
  );

  const buttonContent = (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5"
        viewBox="0 0 448 512"
      >
        <path
          fill="currentColor"
          d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
        ></path>
      </svg>
      <span className="leading-none text-current">{buttonText}</span>
    </>
  );

  const variants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.5,
        stiffness: 100,
      },
    },
  };

  const onSubmit = async (data: InsertLeadToPageFormSchemaType) => {
    try {
      const link = await handleSubmit(data);
      setLink(link);
      window.open(link, '_blank');
    } catch (error: unknown) {
      // No local handling required: the parent handles failures in its handleSubmit
      // (sets error/success UI state and passes `error`/`success` props down).
      // We only log here for debugging.
      // Try catch is needed to avoid unhandled rejection warnings
      console.error(error);
    }
  };

  return (
    <div className="max-w-lg space-y-8">
      {children}
      <motion.div initial="hidden" animate="visible" variants={variants}>
        <form
          className="space-y-8 overflow-hidden rounded-2xl border border-white/20 bg-[#787878]/20 px-4 py-8 backdrop-blur-lg md:px-8"
          onSubmit={form.handleSubmit(onSubmit, onFormError)}
        >
          {success ? (
            <p
              className={clsx(
                'mx-auto text-center tracking-tight',
                getFontVariable(fontFamily),
              )}
            >
              {language === 'de'
                ? 'Deine Handynummer wurde erfolgreich registriert.'
                : 'Your phone number has been successfully registered.'}
              <br />
              {language === 'de'
                ? 'Du wirst weitergeleitet!'
                : 'You will be redirected!'}
            </p>
          ) : (
            <>
              <div className="flex flex-col space-y-4">
                {/* MOBILE PHONE FIELD */}
                <Controller
                  name="phone"
                  control={form.control}
                  render={({ field }) => (
                    <PhoneNumberInput
                      placeholder="+49 176 1234 1234"
                      label={getLabelForField(
                        PageFieldsetFieldTypeEnum.MOBILE_PHONE,
                        language,
                      )}
                      error={errors.phone?.message}
                      className={getFontVariable(fontFamily)}
                      roundedClassName={inputRadiusClassName}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      countryCode={countryCode}
                    />
                  )}
                />

                {/* FIRST / LAST NAME FIELDS */}
                {fieldFirstName && fieldLastName && (
                  <div className="flex items-center gap-4">
                    <TextField
                      type="text"
                      placeholder={getPlaceholderForField(
                        fieldFirstName.type,
                        !fieldFirstName.required,
                        language,
                      )}
                      error={errors.firstName?.message}
                      className={getFontVariable(fontFamily)}
                      roundedClassName={inputRadiusClassName}
                      maxLength={FIRST_NAME_MAX_LENGTH}
                      {...register('firstName')}
                    />
                    <TextField
                      type="text"
                      placeholder={getPlaceholderForField(
                        fieldLastName.type,
                        !fieldLastName.required,
                        language,
                      )}
                      error={errors.lastName?.message}
                      className={getFontVariable(fontFamily)}
                      roundedClassName={inputRadiusClassName}
                      maxLength={LAST_NAME_MAX_LENGTH}
                      {...register('lastName')}
                    />
                  </div>
                )}

                {/* EMAIL FIELD */}
                {fieldEmail && (
                  <TextField
                    type="email"
                    placeholder={getPlaceholderForField(
                      fieldEmail.type,
                      !fieldEmail.required,
                      language,
                    )}
                    error={errors.email?.message}
                    className={getFontVariable(fontFamily)}
                    roundedClassName={inputRadiusClassName}
                    {...register('email')}
                  />
                )}

                {/* ZIP FIELD */}
                {fieldZip && (
                  <TextField
                    type="text"
                    placeholder={getPlaceholderForField(
                      fieldZip.type,
                      !fieldZip.required,
                      language,
                    )}
                    error={errors.zip?.message}
                    className={getFontVariable(fontFamily)}
                    roundedClassName={inputRadiusClassName}
                    {...register('zip')}
                  />
                )}

                {/* CITY FIELD */}
                {fieldCity && (
                  <TextField
                    type="text"
                    placeholder={getPlaceholderForField(
                      fieldCity.type,
                      !fieldCity.required,
                      language,
                    )}
                    error={errors.city?.message}
                    className={getFontVariable(fontFamily)}
                    roundedClassName={inputRadiusClassName}
                    {...register('city')}
                  />
                )}

                {/* STREET / HOUSE NUMBER FIELDS */}
                {fieldStreet && fieldHouseNumber && (
                  <div className="flex items-center gap-4">
                    <TextField
                      type="text"
                      placeholder={getPlaceholderForField(
                        fieldStreet.type,
                        !fieldStreet.required,
                        language,
                      )}
                      error={errors.street?.message}
                      className={getFontVariable(fontFamily)}
                      roundedClassName={inputRadiusClassName}
                      maxLength={STREET_MAX_LENGTH}
                      {...register('street')}
                    />
                    <TextField
                      type="text"
                      placeholder={getPlaceholderForField(
                        fieldHouseNumber.type,
                        !fieldHouseNumber.required,
                        language,
                      )}
                      error={errors.houseNumber?.message}
                      className={getFontVariable(fontFamily)}
                      roundedClassName={inputRadiusClassName}
                      maxLength={HOUSE_NUMBER_MAX_LENGTH}
                      {...register('houseNumber')}
                    />
                  </div>
                )}
              </div>
              <p
                className={clsx(
                  'mx-auto text-center text-[10px] leading-normal font-light opacity-80',
                )}
              >
                {language === 'de'
                  ? `Wenn du auf den Button "${buttonText}" klickst, bestätigst Du, dass Du Nachrichten von ${artistDisplayName} und ${organizationName} über WhatsApp erhalten möchtest. Dafür werden deine Daten mit ${artistDisplayName} und ${organizationName} geteilt. Du kannst die Community jederzeit über die Chat-Einstellungen verlassen.`
                  : `If you click on the button "${buttonText}", you confirm that you want to receive messages from ${artistDisplayName} and ${organizationName} via WhatsApp. For this purpose, your data will be shared with ${artistDisplayName} and ${organizationName}. You can always leave the chat settings.`}
                <br />
                <br />
                {language === 'de'
                  ? 'Weitere Infos zum Umgang mit deinen Daten findest du unter'
                  : 'Additional information on how to handle your data can be found under'}{' '}
                <Link
                  target={externalPrivacyPolicyUrl ? '_blank' : undefined}
                  href={
                    externalPrivacyPolicyUrl ??
                    (language === 'de' ? '/datenschutz' : '/en/privacy-policy')
                  }
                  className="underline"
                >
                  {language === 'de'
                    ? 'Sicherheit und Datenschutz'
                    : 'Security and Privacy'}
                </Link>
                .
              </p>
            </>
          )}

          {error && (
            <div className="w-full text-center text-sm text-red-600">
              {error}
            </div>
          )}

          {incentiveType === 'whatsapp_channel' ? (
            <div className="flex items-center gap-4 rounded-lg bg-red-100 px-4 py-2">
              <CircleAlertIcon className="size-6 text-red-400" />
              <div>
                <p className="text-sm text-red-400">
                  Achtung: Klicke in WhatsApp erneut auf &quot;Abonnieren&quot;,
                  um dem Kanal zu folgen!
                </p>
              </div>
            </div>
          ) : null}

          {success && link ? (
            <Link href={link} target="_blank" className={buttonStyles}>
              {buttonContent}
            </Link>
          ) : (
            <Button
              type="submit"
              className={clsx(
                'mx-auto',
                getRoundedClassName(buttonRadiusClassName),
                getFontVariable(fontFamily),
              )}
              disabled={isSubmitting}
            >
              {buttonContent}
            </Button>
          )}
        </form>
      </motion.div>
    </div>
  );
};
